import { getFullName } from "@/entities/people/helpers";
import {
  EntityMapping,
  Reminder,
  ReminderTimePeriod,
  TaskEntity,
  TaskFormData,
  TaskState
} from "@/entities/tasks/types";
import { getEntityTypeByEntityId } from "@/entities/tasks/taskLinks";
import { EntityData } from "@/services/api/types";
import { ENTITY_NAME_TO_PREFIX } from "@/services/api/entityPrefixes";
import { EntityType } from "@/services/hooks/entities/entityTypes";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import { getEnumTKey, mapEnum } from "@/utils/strings";
import { TFunction } from "i18next";
import { maxBy, toLower } from "lodash";
import { ColumnData } from "@/entities/dataTables/types";

const TASKS_TRANSLATION_KEY = "focus.tasks";

// Columns the user can toggle/reorder from the Display panel. "title" and the
// row-actions column are structural and always shown, so they're excluded here.
export const getDefaultTaskColumnsState = (
  t: TFunction<"translation", undefined>,
  selectedTab: string
): ColumnData[] => {
  const dueOrCompletedColumn =
    selectedTab === TaskState.OPEN
      ? { id: "dueDate", label: t(`${TASKS_TRANSLATION_KEY}.table.dueDate`) }
      : {
          id: "completedAt",
          label: t(`${TASKS_TRANSLATION_KEY}.table.completedAt`)
        };

  const columns =
    selectedTab === TaskState.OPEN
      ? [
          {
            id: "entityNames",
            label: t(`${TASKS_TRANSLATION_KEY}.table.entityNames`)
          },
          dueOrCompletedColumn,
          {
            id: "isHighPriority",
            label: t(`${TASKS_TRANSLATION_KEY}.table.isHighPriority`)
          },
          {
            id: "reminders",
            label: t(`${TASKS_TRANSLATION_KEY}.table.reminders`)
          },
          {
            id: "attachments",
            label: t(`${TASKS_TRANSLATION_KEY}.table.attachments`)
          },
          {
            id: "assigneeId",
            label: t(`${TASKS_TRANSLATION_KEY}.table.assigneeId`)
          },
          {
            id: "createdBy",
            label: t(`${TASKS_TRANSLATION_KEY}.table.createdBy`)
          }
        ]
      : [
          {
            id: "entityNames",
            label: t(`${TASKS_TRANSLATION_KEY}.table.entityNames`)
          },
          {
            id: "isHighPriority",
            label: t(`${TASKS_TRANSLATION_KEY}.table.isHighPriority`)
          },
          {
            id: "reminders",
            label: t(`${TASKS_TRANSLATION_KEY}.table.reminders`)
          },
          {
            id: "attachments",
            label: t(`${TASKS_TRANSLATION_KEY}.table.attachments`)
          },
          {
            id: "assigneeId",
            label: t(`${TASKS_TRANSLATION_KEY}.table.assigneeId`)
          },
          {
            id: "createdBy",
            label: t(`${TASKS_TRANSLATION_KEY}.table.createdBy`)
          },
          dueOrCompletedColumn,
          {
            id: "completedBy",
            label: t(`${TASKS_TRANSLATION_KEY}.table.completedBy`)
          }
        ];

  return columns.map((column, index) => ({
    ...column,
    isVisible: true,
    order: index
  }));
};

export const reminderTimePeriods = mapEnum(
  ReminderTimePeriod,
  (value) => value
);

export const formatReminder = (
  reminder: Reminder,
  t: TFunction<"translation", undefined>
) => {
  if (reminder?.customDate) {
    return simpleDate(toLocalDate(reminder.customDate), "short", true, true);
  }
  let periodText = toLower(
    t(
      getEnumTKey(
        "tasks",
        "timePeriods",
        ReminderTimePeriod,
        reminder?.timePeriod as number
      )
    )
  );
  if (reminder?.value === 1) {
    periodText = periodText.slice(0, -1);
  }
  return `${reminder?.value} ${periodText} before`;
};

const getLargestValueReminder = (reminders: Reminder[]) => {
  if (!reminders || reminders.length === 0) {
    return null;
  }

  return (
    maxBy(reminders, (reminder) => {
      // For custom dates, calculate the number of days from now
      if (reminder.customDate) {
        const now = new Date();
        const customDate = new Date(reminder.customDate);
        // Convert the difference to days
        return (
          Math.abs(now.getTime() - customDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      }

      // For days and weeks, convert everything to days for comparison
      if (reminder.value !== null && reminder.timePeriod !== null) {
        switch (reminder.timePeriod) {
          case ReminderTimePeriod.DAYS:
            return reminder.value;
          case ReminderTimePeriod.WEEKS:
            return reminder.value * 7;
          default:
            return 0;
        }
      }

      return 0;
    }) || null
  );
};

export const displaySelectedReminders = (
  reminders: Reminder[],
  t: TFunction<"translation", undefined>
) => {
  const displayedReminder =
    reminders.length === 1 ? reminders[0] : getLargestValueReminder(reminders);
  if (displayedReminder) {
    return `${formatReminder(displayedReminder, t)}${reminders.length > 1 ? ` + ${reminders.length - 1}` : ""}`;
  }
  return "";
};

export const getRelatedToFieldNames = (
  entityType: EntityType | undefined,
  entityMapping: EntityMapping,
  linkToField: keyof TaskFormData
) => {
  const dealField = entityMapping[EntityType.DEAL]?.field;
  const companyField = entityMapping[EntityType.COMPANY]?.field;
  const personField = entityMapping[EntityType.PERSON]?.field;
  const leadField = entityMapping[EntityType.LEAD]?.field;
  const contractField = entityMapping[EntityType.CONTRACT]?.field;
  if (!dealField || !companyField || !personField || !leadField) return [];

  const defaultRelatedToFieldNames = [companyField, personField, linkToField];

  let relatedToFieldNames = !entityType
    ? defaultRelatedToFieldNames
    : [dealField, companyField, personField];
  if (entityType === EntityType.LEAD) {
    relatedToFieldNames = [leadField, companyField, personField];
  } else if (entityType === EntityType.PERSON) {
    relatedToFieldNames = [personField, companyField, linkToField];
  } else if (entityType === EntityType.COMPANY) {
    relatedToFieldNames = [companyField, personField, linkToField];
  } else if (entityType === EntityType.CONTRACT && contractField) {
    relatedToFieldNames = [contractField, companyField, personField];
  }

  return relatedToFieldNames;
};

export const getDefaultEntityIds = (
  entityType: EntityType,
  entityData: EntityData | undefined
) => {
  const dealId = entityType === EntityType.DEAL ? entityData?.id : "";
  const leadId = entityType === EntityType.LEAD ? entityData?.id : "";
  const companyId =
    entityType === EntityType.COMPANY
      ? entityData?.id || ""
      : entityData?.companyId || "";
  const contactId = entityType === EntityType.PERSON ? entityData?.id : "";
  const contractId = entityType === EntityType.CONTRACT ? entityData?.id : "";

  return {
    dealId,
    leadId,
    companyId,
    contactId,
    contractId
  };
};

/**
 * Same prefix lookup as getEntityTypeByEntityId, narrowed to the types the
 * caller's mapping actually renders.
 */
export const getEntityTypeById = (
  entityId: string,
  entityMapping: EntityMapping
): EntityType | undefined => {
  const entityType = getEntityTypeByEntityId(entityId);
  return entityType !== undefined && entityMapping[entityType]
    ? entityType
    : undefined;
};

/**
 * Resolve which entities to display for a task.
 * Uses the same logic as the edit modal: picks one of deal/lead (deal wins),
 * plus company and person. entityNames provides display names, entityIds is
 * the source of truth.
 *
 * Single source of logic for both the task panel and the edit modal.
 */
export const resolveTaskEntities = (
  entityNames: TaskEntity[] | null | undefined,
  entityIds: string[] | null | undefined
): TaskEntity[] => {
  if (!entityNames || !entityIds) return [];

  // Build a lookup from entityIds to entityNames
  const nameMap = new Map(
    entityNames.map((entity) => [entity.entityId, entity])
  );

  const { deals, leads, companies, people } = ENTITY_NAME_TO_PREFIX;

  // Same priority as the modal: deal wins over lead, plus company and person
  const hasDeal = entityIds.some((id) => id.startsWith(deals));
  const visiblePrefixes = new Set([companies, people]);
  visiblePrefixes.add(hasDeal ? deals : leads);

  return entityIds
    .filter((id) => {
      const prefix = id.split("-")[0] + "-";
      return visiblePrefixes.has(prefix) && nameMap.has(id);
    })
    .reduce<TaskEntity[]>((result, id) => {
      const entity = nameMap.get(id);
      if (!entity) return result;
      if (id.startsWith(people)) {
        const [firstName = "", lastName = ""] = entity.entityName.split(";");
        result.push({ ...entity, entityName: getFullName({ firstName, lastName }) });
      } else {
        result.push(entity);
      }
      return result;
    }, []);
};

/**
 * Build a prefix→option map from resolved task entities.
 * Used for immediate dropdown display in the edit modal.
 */
export const buildEntityNameOptions = (
  entityNames: TaskEntity[] | null | undefined,
  entityIds: string[] | null | undefined
): Record<string, { id: string; displayName: string }> => {
  const resolved = resolveTaskEntities(entityNames, entityIds);
  const result: Record<string, { id: string; displayName: string }> = {};
  resolved.forEach((entity) => {
    const prefix = entity.entityId.split("-")[0] + "-";
    result[prefix] = { id: entity.entityId, displayName: entity.entityName };
  });
  return result;
};
